import React, { createContext, useState, useMemo, useCallback, useEffect } from 'react';

// 1. Create the Context
const BeamContext = createContext(null);

/** Snapshot of inputs that define an FEA run — used to detect stale results vs current form. */
export function buildBeamInputsKey({
  spans,
  advancedOptions,
  selectedCrossSectionId,
  materialFamily,
  selectedType,
}) {
  return JSON.stringify({
    spans,
    advancedOptions,
    selectedCrossSectionId,
    materialFamily,
    selectedType,
  });
}

// 2. Define Initial State (can be expanded)
const initialState = {
  spans: [
    {
      length: '1',
      leftBoundary: 'pinned',
      rightBoundary: 'pinned',
      leftTorsionSupport: true,
      rightTorsionSupport: true,
      leftWarpingSupport: false,
      rightWarpingSupport: false,
      loads: [],
      leftSpringCoefficient: '',
      rightSpringCoefficient: '',
      leftSpringAxial: '',
      leftSpringTorsion: '',
      leftSpringWarping: '',
      rightSpringAxial: '',
      rightSpringTorsion: '',
      rightSpringWarping: '',
    }
  ],
  selectedType: 'CFSHS', // Default section type filter
  materialFamily: 'steel',
  selectedCrossSectionId: '',
  crossSections: [],
  results: null,
  loading: { // More granular loading state
    sections: false,
    calculation: false,
  }, 
  error: { // More granular error state
    sections: null,
    calculation: null,
  },
  formErrors: {},
  notification: { open: false, message: '', severity: 'info' },
  advancedOptions: {
    expanded: false,
    steelGrade: 'S355',
    steelEOverrideGpa: '',
    unbracedLengthM: '',
    ltbEffectiveLengthM: '',
    N_Ed_kN: '',
    L_cr_y_m: '',
    L_cr_z_m: '',
    deflectionLimitRatio: '250',
    loadsBasis: 'design', // 'design' | 'characteristic' — characteristic enables ULS/SLS auto partial factors
    enableEc3Checks: false,
    concreteGrade: 'C30/37',
    concreteSteelGrade: 'B500B',
    concreteSectionShape: 'rectangular',
    invertedTbfM: '1.20',
    invertedThfM: '0.20',
    invertedTbwM: '0.30',
    invertedThwM: '0.50',
    concretePrestressType: 'none',
    prestressTendonPreset: '12.7-1860',
    prestressNTendons: 0,
    prestressTensionMpa: '1000',
    prestressTendonCoverMm: 30,
    concreteWidthM: '0.30',
    concreteHeightM: '0.50',
    concreteCoverM: '0.03',
    concreteBarDiamMm: '16',
    concreteBarCount: '2',
    concreteAutoRebar: true,
    exposureClass: 'XC1',
    analysisType: 'linear',
    timberStrengthClass: 'C24',
    timberServiceClass: '1',
    timberWidthM: '0.09',
    timberHeightM: '0.20',
    enableEc5Checks: false,
    loadDurationClass: 'medium_term',
    psi2: '0.3',
    enableEc2Checks: true,
    selfWeight: true,
    eurocodeNationalAnnex: 'finland', // 'finland' | 'nl' — EC2 α_cc, V_Rd,c, crack w_lim
  },

};


const CONCRETE_SECTION_PRESETS = [
  { id: 101, name: 'RC 200×400', type: 'concrete', width_m: 0.2, height_m: 0.4 },
  { id: 102, name: 'RC 250×500', type: 'concrete', width_m: 0.25, height_m: 0.5 },
  { id: 103, name: 'RC 300×500', type: 'concrete', width_m: 0.3, height_m: 0.5 },
  { id: 104, name: 'RC 300×600', type: 'concrete', width_m: 0.3, height_m: 0.6 },
  { id: 105, name: 'RC 400×800', type: 'concrete', width_m: 0.4, height_m: 0.8 },
];

const WOOD_SECTION_PRESETS = [
  { id: 201, name: 'GL 90×270', type: 'WOOD', section_name: 'GL90x270' },
  { id: 202, name: 'GL 115×360', type: 'WOOD', section_name: 'GL115x360' },
  { id: 203, name: 'GL 140×450', type: 'WOOD', section_name: 'GL140x450' },
  { id: 204, name: 'Sawn 48×98', type: 'WOOD', section_name: 'Sawn48x98' },
  { id: 205, name: 'Sawn 48×123', type: 'WOOD', section_name: 'Sawn48x123' },
  { id: 206, name: 'Sawn 48×148', type: 'WOOD', section_name: 'Sawn48x148' },
  { id: 207, name: 'Sawn 48×173', type: 'WOOD', section_name: 'Sawn48x173' },
  { id: 208, name: 'Sawn 48×198', type: 'WOOD', section_name: 'Sawn48x198' },
  { id: 209, name: 'Sawn 48×223', type: 'WOOD', section_name: 'Sawn48x223' },
  { id: 210, name: 'Sawn 48×248', type: 'WOOD', section_name: 'Sawn48x248' },
  { id: 211, name: 'Kerto-S 45×200', type: 'WOOD', section_name: 'KertoS45x200' },
  { id: 212, name: 'Kerto-S 45×220', type: 'WOOD', section_name: 'KertoS45x220' },
  { id: 213, name: 'Kerto-S 45×240', type: 'WOOD', section_name: 'KertoS45x240' },
  { id: 214, name: 'Kerto-S 45×260', type: 'WOOD', section_name: 'KertoS45x260' },
  { id: 215, name: 'Kerto-S 45×300', type: 'WOOD', section_name: 'KertoS45x300' },
  { id: 216, name: 'Kerto-S 45×360', type: 'WOOD', section_name: 'KertoS45x360' },
  { id: 217, name: 'Kerto-S 45×400', type: 'WOOD', section_name: 'KertoS45x400' },
  { id: 218, name: 'Kerto-S 45×450', type: 'WOOD', section_name: 'KertoS45x450' },
  { id: 219, name: 'Kerto-S 45×600', type: 'WOOD', section_name: 'KertoS45x600' },
  { id: 220, name: 'Kerto-S 51×200', type: 'WOOD', section_name: 'KertoS51x200' },
  { id: 221, name: 'Kerto-S 51×220', type: 'WOOD', section_name: 'KertoS51x220' },
  { id: 222, name: 'Kerto-S 51×240', type: 'WOOD', section_name: 'KertoS51x240' },
  { id: 223, name: 'Kerto-S 51×260', type: 'WOOD', section_name: 'KertoS51x260' },
  { id: 224, name: 'Kerto-S 51×300', type: 'WOOD', section_name: 'KertoS51x300' },
  { id: 225, name: 'Kerto-S 51×360', type: 'WOOD', section_name: 'KertoS51x360' },
  { id: 226, name: 'Kerto-S 51×400', type: 'WOOD', section_name: 'KertoS51x400' },
  { id: 227, name: 'Kerto-S 51×450', type: 'WOOD', section_name: 'KertoS51x450' },
  { id: 228, name: 'Kerto-S 51×600', type: 'WOOD', section_name: 'KertoS51x600' },
  { id: 229, name: 'Kerto-S 57×200', type: 'WOOD', section_name: 'KertoS57x200' },
  { id: 230, name: 'Kerto-S 57×220', type: 'WOOD', section_name: 'KertoS57x220' },
  { id: 231, name: 'Kerto-S 57×240', type: 'WOOD', section_name: 'KertoS57x240' },
  { id: 232, name: 'Kerto-S 57×260', type: 'WOOD', section_name: 'KertoS57x260' },
  { id: 233, name: 'Kerto-S 57×300', type: 'WOOD', section_name: 'KertoS57x300' },
  { id: 234, name: 'Kerto-S 57×360', type: 'WOOD', section_name: 'KertoS57x360' },
  { id: 235, name: 'Kerto-S 57×400', type: 'WOOD', section_name: 'KertoS57x400' },
  { id: 236, name: 'Kerto-S 57×450', type: 'WOOD', section_name: 'KertoS57x450' },
  { id: 237, name: 'Kerto-S 57×600', type: 'WOOD', section_name: 'KertoS57x600' },
  { id: 238, name: 'Kerto-S 63×200', type: 'WOOD', section_name: 'KertoS63x200' },
  { id: 239, name: 'Kerto-S 63×220', type: 'WOOD', section_name: 'KertoS63x220' },
  { id: 240, name: 'Kerto-S 63×240', type: 'WOOD', section_name: 'KertoS63x240' },
  { id: 241, name: 'Kerto-S 63×260', type: 'WOOD', section_name: 'KertoS63x260' },
  { id: 242, name: 'Kerto-S 63×300', type: 'WOOD', section_name: 'KertoS63x300' },
  { id: 243, name: 'Kerto-S 63×360', type: 'WOOD', section_name: 'KertoS63x360' },
  { id: 244, name: 'Kerto-S 63×400', type: 'WOOD', section_name: 'KertoS63x400' },
  { id: 245, name: 'Kerto-S 63×450', type: 'WOOD', section_name: 'KertoS63x450' },
  { id: 246, name: 'Kerto-S 63×600', type: 'WOOD', section_name: 'KertoS63x600' },
];

// 3. Create the Provider Component
export const BeamProvider = ({ children }) => {
  const [spans, setSpans] = useState(initialState.spans);
  const [selectedType, setSelectedType] = useState(initialState.selectedType);
  const [selectedCrossSectionId, setSelectedCrossSectionId] = useState(initialState.selectedCrossSectionId);
  const [crossSections, setCrossSections] = useState(initialState.crossSections);
  const [results, setResults] = useState(initialState.results);
  const [loading, setLoading] = useState(initialState.loading);
  const [error, setError] = useState(initialState.error);
  const [formErrors, setFormErrors] = useState(initialState.formErrors);
  const [notification, setNotification] = useState(initialState.notification);
  const [materialFamily, setMaterialFamily] = useState(initialState.materialFamily);
  const [advancedOptions, setAdvancedOptions] = useState(initialState.advancedOptions);
  const [lastSuccessfulCalcInputsKey, setLastSuccessfulCalcInputsKey] = useState(null);

  const setAdvancedOption = useCallback((key, value) => {
    setAdvancedOptions(prev => ({ ...prev, [key]: value }));
  }, []);

  // Log state values just before selectedSection is derived
  console.log('BeamProvider RENDER: selectedCrossSectionId:', selectedCrossSectionId, 'crossSections count:', crossSections.length);

  // --- Derived State ---
  const totalBeamLength = useMemo(() => {
    return spans.reduce((sum, span) => sum + (parseFloat(span.length) || 0), 0);
  }, [spans]);

  const beamInputsKey = useMemo(
    () =>
      buildBeamInputsKey({
        spans,
        advancedOptions,
        selectedCrossSectionId,
        materialFamily,
        selectedType,
      }),
    [spans, advancedOptions, selectedCrossSectionId, materialFamily, selectedType],
  );

  const resultsOutOfDate = useMemo(
    () =>
      results != null &&
      lastSuccessfulCalcInputsKey != null &&
      beamInputsKey !== lastSuccessfulCalcInputsKey,
    [results, lastSuccessfulCalcInputsKey, beamInputsKey],
  );

  const selectedSection = useMemo(() => {
    const idToFind = parseInt(selectedCrossSectionId, 10);
    // console.log(`BeamContext: Deriving selectedSection. ID to find: ${idToFind} (from '${selectedCrossSectionId}')`);

    if (isNaN(idToFind)) {
      // console.log('BeamContext: idToFind is NaN, returning undefined for selectedSection.');
      return undefined;
    }

    if (!crossSections || crossSections.length === 0) {
      // console.log('BeamContext: crossSections array is empty or null, returning undefined.');
      return undefined;
    }

    console.log(`BeamContext (selectedSection useMemo): Trying to find ID: ${idToFind}. Available IDs in crossSections (sample of first 5):`, 
                crossSections.slice(0, 5).map(s => ({ id: s.id, type: typeof s.id })) );
    
    const foundSection = crossSections.find(s => s.id === idToFind);
    
    console.log('BeamContext (selectedSection useMemo): Result of find operation (foundSection):', foundSection);

    if (!foundSection) {
      console.warn(`BeamContext (selectedSection useMemo): Section with ID ${idToFind} NOT FOUND in crossSections array.`);
      if (crossSections.length < 200) { 
          console.log('BeamContext (selectedSection useMemo): All available IDs in current crossSections:', crossSections.map(s => s.id));
      }
    }
    else {
      console.log('BeamContext (selectedSection useMemo): Successfully found section:', foundSection);
    }

    return foundSection;
  } , [crossSections, selectedCrossSectionId]);

  // Keep advanced b×h in sync with concrete preset so edits apply to FEA + EC2 (ductility x/d, d, I).
  useEffect(() => {
    if (materialFamily !== 'concrete') return;
    const sec = CONCRETE_SECTION_PRESETS.find((s) => String(s.id) === String(selectedCrossSectionId));
    if (!sec || sec.width_m == null || sec.height_m == null) return;
    setAdvancedOptions((prev) => ({
      ...prev,
      concreteWidthM: String(sec.width_m),
      concreteHeightM: String(sec.height_m),
    }));
  }, [materialFamily, selectedCrossSectionId]);

  // --- Data Fetching and Side Effects ---

  // Effect 1: Fetch cross-section names when selectedType changes (steel only)
  useEffect(() => {
    if (materialFamily !== 'steel') {
      return;
    }
    if (!selectedType || selectedType === 'all') { // Don't fetch if 'all' or invalid type
       // If type becomes 'all', potentially load all sections or reset?
       // For now, just clear sections if not 'all' initially
       // Or fetch a master list if 'all' is selected? Assuming static files per type.
       if (selectedType === 'all') {
           console.warn("'all' selected, fetching logic might need adjustment based on available data.");
           // setCrossSections([]); // Decide how to handle 'all'
       }
       return; 
    }

    const fetchCrossSectionNames = async () => {
      setLoading(prev => ({ ...prev, sections: true }));
      setError(prev => ({ ...prev, sections: null })); // Clear previous section error
      try {
        // Use the per-type static JSON file (assuming it's in public/)
        console.log(`Fetching /${selectedType}_names.json`);
        const response = await fetch(`/${selectedType}_names.json`); 
        if (!response.ok) {
          throw new Error(`Failed to fetch cross-section names for type ${selectedType} (status: ${response.status})`);
        }
        const data = await response.json();
        setCrossSections(data); 
        
        // If no section is currently selected, select the first one from the fetched list
        // Avoid resetting selection if one already exists and is valid for the new type
        const currentSelectionValid = data.some(s => s.id === selectedCrossSectionId);
        if (data.length > 0 && !currentSelectionValid) { 
          console.log("Setting initial/default section ID for new type:", data[0].id);
          setSelectedCrossSectionId(data[0].id);
        } else if (data.length === 0) {
            setSelectedCrossSectionId(''); // Clear selection if type has no sections
        }

      } catch (err) {
        console.error("Fetching cross sections failed:", err);
        const errorMsg = err.message || `Failed to load ${selectedType} sections.`;
        setError(prev => ({ ...prev, sections: errorMsg }));
        setNotification({ open: true, message: errorMsg, severity: 'error' });
        setCrossSections([]); // Clear sections on error
        setSelectedCrossSectionId(''); // Clear selection on error
      } finally {
        setLoading(prev => ({ ...prev, sections: false }));
      }
    };

    fetchCrossSectionNames();
  }, [selectedType, materialFamily]);

  // Effect 1b: Concrete / timber preset lists (no JSON fetch)
  useEffect(() => {
    if (materialFamily === 'concrete') {
      setCrossSections(CONCRETE_SECTION_PRESETS);
      setSelectedType('concrete');
      setSelectedCrossSectionId(String(CONCRETE_SECTION_PRESETS[0].id));
      return;
    }
    if (materialFamily === 'wood') {
      setCrossSections(WOOD_SECTION_PRESETS);
      setSelectedType('WOOD');
      setSelectedCrossSectionId(String(WOOD_SECTION_PRESETS[0].id));
    }
  }, [materialFamily]);

  useEffect(() => {
    if (materialFamily === 'steel' && (selectedType === 'concrete' || selectedType === 'WOOD')) {
      setSelectedType('CFSHS');
    }
  }, [materialFamily, selectedType]);

  // Effect 2: Check localStorage for preset ID when sections load
  useEffect(() => {
      const presetIdString = localStorage.getItem('calculateCrossSectionId');
      if (presetIdString) {
          console.log("Found preset cross section ID string in localStorage:", presetIdString);
          const presetIdNumber = parseInt(presetIdString, 10);
          
          if (isNaN(presetIdNumber)) {
             console.warn(`Invalid preset ID found in localStorage: ${presetIdString}. Removing.`);
             localStorage.removeItem('calculateCrossSectionId');
             return;
          }
          console.log("Parsed preset ID number:", presetIdNumber);

          // IMPORTANT: Only proceed if crossSections are loaded
          if (crossSections.length > 0) { 
              const presetSection = crossSections.find(s => s.id === presetIdNumber);
              if (presetSection) {
                 console.log("Preset section found:", presetSection);
                 // Check if update is actually needed to prevent potential loops
                 if (selectedCrossSectionId !== presetIdNumber || selectedType !== presetSection.type) {
                    console.log("Applying preset cross section ID and type.");
                    // Update type first, which triggers the fetch effect if needed
                    // Then update the ID
                    setMaterialFamily('steel');
                    setSelectedType(presetSection.type); 
                    setSelectedCrossSectionId(presetIdNumber); 
                 } else {
                    console.log("Preset matches current state, no update needed.");
                 }
                 // Always remove item after processing
                 localStorage.removeItem('calculateCrossSectionId'); 
              } else {
                  console.warn(`Preset ID ${presetIdNumber} not found in loaded sections for type ${selectedType}. Removing.`);
                  localStorage.removeItem('calculateCrossSectionId'); 
              }
          } else {
             console.log("Cross sections not loaded yet, localStorage check will re-run when they load.");
             // Don't remove item, wait for crossSections
          }
      }
      // Dependency: Run when crossSections array is populated/updated
  }, [crossSections, selectedCrossSectionId, selectedType]);

   // Effect 3: Update selected cross section when type filter changes MANUALLY (and no preset)
   useEffect(() => {
     if (materialFamily !== 'steel') {
       return;
     }
     // *** EXIT if a preset ID is currently in localStorage ***
     if (localStorage.getItem('calculateCrossSectionId')) {
         console.log("Preset ID found in localStorage, skipping manual type change effect.");
         return; 
     }
     
     console.log(`Manual type change to ${selectedType} detected (no preset active).`);
     if (crossSections.length > 0) {
       // Find the first section matching the new type filter
       const firstSectionOfType = crossSections.find(section => section.type === selectedType);
       const firstSectionOverall = crossSections[0]; // Fallback if type has no sections
       
       let sectionToSelect = null;
       if (selectedType === 'all') {
          // If 'all' is selected, keep the current selection if it exists, otherwise default to first overall
          const currentSectionStillExists = crossSections.some(s => s.id === selectedCrossSectionId);
          sectionToSelect = currentSectionStillExists ? crossSections.find(s => s.id === selectedCrossSectionId) : firstSectionOverall;
       } else {
           sectionToSelect = firstSectionOfType; // Use first of specific type if found
       }
       
       if (sectionToSelect) {
          // Only update if the target ID is different from the current one
          if (sectionToSelect.id !== selectedCrossSectionId) { 
             console.log("Setting selected ID based on manual type change:", sectionToSelect.id);
             setSelectedCrossSectionId(sectionToSelect.id);
          } else {
              console.log("First section of type/current section already selected, no change needed.");
          }
       } else {
          // If no sections match the specific type filter (and not 'all'), clear selection
          if (selectedType !== 'all' && selectedCrossSectionId !== '') {
             console.log(`No sections found for type ${selectedType}, clearing selection.`);
             setSelectedCrossSectionId('');
          }
       }
     } else if (selectedCrossSectionId !== '') {
         // If crossSections becomes empty (e.g., fetch error), clear selection
         console.log("Cross sections empty, clearing selection.")
         setSelectedCrossSectionId('');
     }
     // Run only when selectedType changes manually OR crossSections load (and no preset)
   }, [selectedType, crossSections, materialFamily]);

  // --- Updater Functions ---

  const updateSpanLength = useCallback((index, value) => {
    const numericValue = value.replace(',', '.');
    const errorKey = `span-${index}-length`;
    let newErrors = {};

    // Basic validation: prevent negative length
    if (parseFloat(numericValue) < 0 && numericValue !== '') {
       newErrors[errorKey] = "Length cannot be negative";
    } 
    
    setSpans(currentSpans => {
      const newSpans = [...currentSpans];
      newSpans[index] = { ...newSpans[index], length: numericValue };
      return newSpans;
    });

    setFormErrors(prev => {
        const next = {...prev, ...newErrors};
        if (!newErrors[errorKey]) { // Clear error if valid
            delete next[errorKey];
        }
        return next;
    });

  }, [setSpans, setFormErrors]);

  const updateLoad = useCallback((spanIndex, loadIndex, field, value) => {
    const rawValue = String(value).replace(',', '.'); // Ensure string for replace
    
    setSpans(currentSpans => {
      const newSpans = [...currentSpans]; // Work on a fresh copy for validation + update
      const span = newSpans[spanIndex];
      const currentLoad = span?.loads?.[loadIndex];
      
      if (!currentLoad || !span) {
        console.error(`Span or Load not found at span ${spanIndex}, index ${loadIndex}`);
        return currentSpans; // Return original state if load/span not found
      }

      const spanLength = parseFloat(span?.length || 0);
      let isValid = true;
      let newErrors = {}; // Use a local object for validation errors for this specific update
      const errorKeyBase = `span-${spanIndex}-load-${loadIndex}`;

      const parseLoadNum = (v) => {
          if (v === '' || v == null) return null;
          const n = parseFloat(String(v).replace(',', '.'));
          return Number.isFinite(n) ? n : NaN;
      };

      // Perform validation based on field
      if (field === 'position' || field === 'startPosition' || field === 'endPosition') {
          const positionValue = parseFloat(rawValue.replace(',', '.'));
          const isGlobal = currentLoad.type === 'line' && currentLoad.coordinateSystem === 'global';
          const maxPosition = isGlobal ? totalBeamLength : spanLength;
          const errorMessage = isGlobal ? "Location not on beam" : "Location not on span";

          if (rawValue !== '' && isNaN(positionValue)) {
              isValid = false;
              newErrors[`${errorKeyBase}-${field}`] = "Invalid number";
          } else if (rawValue !== '' && !isNaN(positionValue)) {
              if (maxPosition > 0 && (positionValue < 0 || positionValue > maxPosition)) {
                  isValid = false;
                  newErrors[`${errorKeyBase}-${field}`] = errorMessage;
              } else if (currentLoad.type === 'line') {
                  if (field === 'startPosition') {
                      const endParsed = parseLoadNum(currentLoad.endPosition);
                      if (currentLoad.endPosition !== '' && currentLoad.endPosition != null && !Number.isNaN(endParsed)) {
                          if (positionValue >= endParsed) {
                              isValid = false;
                              newErrors[`${errorKeyBase}-startPosition`] = "Start must be < End";
                          }
                      } else if (maxPosition > 0 && positionValue >= maxPosition) {
                          isValid = false;
                          newErrors[`${errorKeyBase}-startPosition`] = isGlobal
                              ? "Start must be < beam end (or enter End)"
                              : "Start must be < span end (or enter End)";
                      }
                  } else if (field === 'endPosition') {
                      const startParsed = parseLoadNum(currentLoad.startPosition);
                      if (currentLoad.startPosition !== '' && currentLoad.startPosition != null && !Number.isNaN(startParsed)) {
                          if (positionValue <= startParsed) {
                              isValid = false;
                              newErrors[`${errorKeyBase}-endPosition`] = "End must be > Start";
                          }
                      } else if (positionValue <= 0) {
                          isValid = false;
                          newErrors[`${errorKeyBase}-endPosition`] = "End must be > 0";
                      }
                  }
              }
          }
      } else if (field === 'coordinateSystem') {
          isValid = true;
      } else if (field === '_updateObject') {
          // Reset potential errors when type changes
          isValid = true; 
      }
      // Add magnitude validation if needed (e.g., must be number)
      else if (field === 'magnitude') {
          if (rawValue !== '' && isNaN(parseFloat(rawValue))) {
              isValid = false;
              newErrors[`${errorKeyBase}-magnitude`] = "Invalid number";
          } else {
              isValid = true; // Assume magnitude is valid unless it's not a number
          }
      }

      // Update form errors state based on validation result
      setFormErrors(prev => {
          const next = {...prev, ...newErrors }; // Apply new errors
          // Clear errors for the current field if it's now valid or type changed
          if (isValid && field !== '_updateObject') {
              delete next[`${errorKeyBase}-${field}`];
              // Also clear related field errors if validation passes
              if (field === 'startPosition') delete next[`${errorKeyBase}-endPosition`];
              if (field === 'endPosition') delete next[`${errorKeyBase}-startPosition`];
          }
          if (field === 'coordinateSystem') {
              delete next[`${errorKeyBase}-startPosition`];
              delete next[`${errorKeyBase}-endPosition`];
          } else if (field === '_updateObject') {
              // Clear all position/magnitude errors for this load on type change
              delete next[`${errorKeyBase}-position`];
              delete next[`${errorKeyBase}-startPosition`];
              delete next[`${errorKeyBase}-endPosition`];
              delete next[`${errorKeyBase}-magnitude`];
          }
          return next;
      });

      // Update spans state
      const modifiableSpans = [...newSpans]; // Create a new copy to modify
      if (field === '_updateObject') {
           modifiableSpans[spanIndex].loads[loadIndex] = { ...value }; // value is the new load object
      } else {
           modifiableSpans[spanIndex].loads[loadIndex] = {
               ...currentLoad,
               [field]: rawValue
           };
      }
      return modifiableSpans; // Return the updated spans array
    });

  }, [setSpans, setFormErrors, totalBeamLength]);

  const updateBoundary = useCallback((spanIndex, boundarySide, value) => {
      setSpans(currentSpans => {
          const newSpans = [...currentSpans];
          const stateKey = boundarySide === 'left' ? 'leftBoundary' : 'rightBoundary';
          const updatedSpan = { ...newSpans[spanIndex], [stateKey]: value };
          if (value === 'fixed') {
            const warpKey = boundarySide === 'left' ? 'leftWarpingSupport' : 'rightWarpingSupport';
            updatedSpan[warpKey] = true;
          }

          // Reset coefficient if boundary type is no longer spring
          const coefficientKey = boundarySide === 'left' ? 'leftSpringCoefficient' : 'rightSpringCoefficient';
          const axialKey = boundarySide === 'left' ? 'leftSpringAxial' : 'rightSpringAxial';
          const torKey = boundarySide === 'left' ? 'leftSpringTorsion' : 'rightSpringTorsion';
          const springWarpKey = boundarySide === 'left' ? 'leftSpringWarping' : 'rightSpringWarping';
          if (value !== 'spring') {
              updatedSpan[coefficientKey] = '';
              updatedSpan[axialKey] = '';
              updatedSpan[torKey] = '';
              updatedSpan[springWarpKey] = '';
          }

          newSpans[spanIndex] = updatedSpan;

          // Update adjacent boundary if needed
          if (stateKey === 'rightBoundary' && spanIndex < newSpans.length - 1) {
              const nextSpan = {
                ...newSpans[spanIndex + 1],
                leftBoundary: value,
                leftTorsionSupport: updatedSpan.rightTorsionSupport !== false,
              };
              if (value === 'fixed') {
                nextSpan.leftWarpingSupport = true;
              }
               // Also reset coefficient of the next span's left if it's no longer inheriting a spring
              if (value !== 'spring') {
                 nextSpan.leftSpringCoefficient = '';
                 nextSpan.leftSpringAxial = '';
                 nextSpan.leftSpringTorsion = '';
                 nextSpan.leftSpringWarping = '';
              }
              newSpans[spanIndex + 1] = nextSpan;
          }
          // If changing left boundary, update previous span's right boundary (if applicable)
          if (stateKey === 'leftBoundary' && spanIndex > 0) {
             const prevSpan = {
               ...newSpans[spanIndex - 1],
               rightBoundary: value,
               rightTorsionSupport: updatedSpan.leftTorsionSupport !== false,
             };
             if (value === 'fixed') {
               prevSpan.rightWarpingSupport = true;
             }
             // Also reset coefficient of the prev span's right if it's no longer inheriting a spring
             if (value !== 'spring') {
                 prevSpan.rightSpringCoefficient = '';
                 prevSpan.rightSpringAxial = '';
                 prevSpan.rightSpringTorsion = '';
                 prevSpan.rightSpringWarping = '';
              }
              newSpans[spanIndex - 1] = prevSpan;
          }

          return newSpans;
      });
  }, [setSpans]);

  const updateTorsionSupport = useCallback((spanIndex, boundarySide, checked) => {
    const key = boundarySide === 'left' ? 'leftTorsionSupport' : 'rightTorsionSupport';
    setSpans((currentSpans) => {
      const newSpans = [...currentSpans];
      const updatedSpan = { ...newSpans[spanIndex], [key]: checked };
      newSpans[spanIndex] = updatedSpan;
      if (key === 'rightTorsionSupport' && spanIndex < newSpans.length - 1) {
        newSpans[spanIndex + 1] = { ...newSpans[spanIndex + 1], leftTorsionSupport: checked };
      }
      if (key === 'leftTorsionSupport' && spanIndex > 0) {
        newSpans[spanIndex - 1] = { ...newSpans[spanIndex - 1], rightTorsionSupport: checked };
      }
      return newSpans;
    });
  }, [setSpans]);

  const updateWarpingSupport = useCallback((spanIndex, boundarySide, checked) => {
    const key = boundarySide === 'left' ? 'leftWarpingSupport' : 'rightWarpingSupport';
    setSpans((currentSpans) => {
      const newSpans = [...currentSpans];
      newSpans[spanIndex] = { ...newSpans[spanIndex], [key]: checked };
      return newSpans;
    });
  }, [setSpans]);

  const updateSpringCoefficient = useCallback((spanIndex, side, value) => {
      const numericValue = value.replace(',', '.');
      if (numericValue !== '' && isNaN(numericValue)) {
          console.warn("Invalid spring coefficient input ignored:", value);
           setNotification({ open: true, message: 'Spring coefficient must be a number.', severity: 'warning' });
          return;
      }

      setSpans(currentSpans => {
          const newSpans = [...currentSpans];
          const coefficientKey = side === 'left' ? 'leftSpringCoefficient' : 'rightSpringCoefficient';
          newSpans[spanIndex] = { ...newSpans[spanIndex], [coefficientKey]: numericValue };
          if (side === 'right' && spanIndex < newSpans.length - 1) {
              newSpans[spanIndex + 1] = {
                  ...newSpans[spanIndex + 1],
                  leftSpringCoefficient: numericValue,
              };
          }
          if (side === 'left' && spanIndex > 0) {
              newSpans[spanIndex - 1] = {
                  ...newSpans[spanIndex - 1],
                  rightSpringCoefficient: numericValue,
              };
          }
          return newSpans;
      });
  }, [setSpans, setNotification]);

  const updateSpringExtra = useCallback((spanIndex, side, field, value) => {
      const numericValue = String(value).replace(',', '.');
      if (numericValue !== '' && isNaN(numericValue)) {
          setNotification({ open: true, message: 'Spring stiffness must be a number.', severity: 'warning' });
          return;
      }
      const keyMap = {
        left: { axial: 'leftSpringAxial', torsion: 'leftSpringTorsion', warping: 'leftSpringWarping' },
        right: { axial: 'rightSpringAxial', torsion: 'rightSpringTorsion', warping: 'rightSpringWarping' },
      };
      const k = keyMap[side][field];
      if (!k) return;
      setSpans((currentSpans) => {
          const newSpans = [...currentSpans];
          newSpans[spanIndex] = { ...newSpans[spanIndex], [k]: numericValue };
          if (side === 'right' && spanIndex < newSpans.length - 1) {
              const lk = field === 'axial' ? 'leftSpringAxial' : field === 'torsion' ? 'leftSpringTorsion' : 'leftSpringWarping';
              newSpans[spanIndex + 1] = { ...newSpans[spanIndex + 1], [lk]: numericValue };
          }
          if (side === 'left' && spanIndex > 0) {
              const rk = field === 'axial' ? 'rightSpringAxial' : field === 'torsion' ? 'rightSpringTorsion' : 'rightSpringWarping';
              newSpans[spanIndex - 1] = { ...newSpans[spanIndex - 1], [rk]: numericValue };
          }
          return newSpans;
      });
  }, [setSpans, setNotification]);

  const addLoadToSpan = useCallback((spanIndex, loadType) => {
      setSpans(currentSpans => {
          const newSpans = [...currentSpans];
          if (newSpans[spanIndex]) {
              let newLoad = {};
              switch(loadType) {
                  case 'line':
                      newLoad = { type: 'line', magnitude: '', startPosition: '0', endPosition: '', coordinateSystem: 'global', loadAction: 'permanent', eccentricityY: '0', eccentricityZ: '0' };
                      break;
                  case 'moment':
                      newLoad = { type: 'moment', magnitude: '', position: '0', loadAction: 'permanent' };
                      break;
                  case 'point':
                  default:
                      newLoad = { type: 'point', magnitude: '', position: '0', loadAction: 'permanent', eccentricityY: '0', eccentricityZ: '0' }; 
              }
              // Ensure loads array exists
              if (!newSpans[spanIndex].loads) {
                  newSpans[spanIndex].loads = [];
              }
              newSpans[spanIndex].loads.push(newLoad);
          }
          return newSpans;
      });
  }, [setSpans]);

  const removeLoadFromSpan = useCallback((spanIndex, loadIndex) => {
      setSpans(currentSpans => {
          const newSpans = [...currentSpans];
          if (newSpans[spanIndex]?.loads?.[loadIndex]) {
              newSpans[spanIndex].loads.splice(loadIndex, 1);
          }
          return newSpans;
      });
  }, [setSpans]);

  const addNewSpan = useCallback(() => {
      setSpans(currentSpans => {
          const newSpans = [...currentSpans];
          const lastSpan = newSpans[newSpans.length - 1];
          newSpans.push({
              length: '',
              leftBoundary: lastSpan ? lastSpan.rightBoundary : 'pinned',
              rightBoundary: 'pinned',
              leftTorsionSupport: lastSpan ? lastSpan.rightTorsionSupport !== false : true,
              rightTorsionSupport: true,
              leftWarpingSupport: false,
              rightWarpingSupport: false,
              loads: [],
              leftSpringCoefficient: lastSpan ? lastSpan.rightSpringCoefficient : '',
              rightSpringCoefficient: '',
              leftSpringAxial: lastSpan ? lastSpan.rightSpringAxial : '',
              leftSpringTorsion: lastSpan ? lastSpan.rightSpringTorsion : '',
              leftSpringWarping: lastSpan ? lastSpan.rightSpringWarping : '',
              rightSpringAxial: '',
              rightSpringTorsion: '',
              rightSpringWarping: '',
          });
          return newSpans;
      });
  }, [setSpans]);
  
  /** Restore UI state from saved analysis `input_data.ui` snapshot. */
  const loadBeamFromSavedInput = useCallback((input_data) => {
    const ui = input_data?.ui;
    if (!ui) return;
    if (ui.spans && Array.isArray(ui.spans)) {
      setSpans(
        ui.spans.map((s) => ({
          ...s,
          leftTorsionSupport:
            s.leftTorsionSupport !== undefined ? s.leftTorsionSupport !== false : s.leftLtbSupport !== false,
          rightTorsionSupport:
            s.rightTorsionSupport !== undefined ? s.rightTorsionSupport !== false : s.rightLtbSupport !== false,
          leftWarpingSupport: s.leftWarpingSupport === true,
          rightWarpingSupport: s.rightWarpingSupport === true,
          leftSpringAxial: s.leftSpringAxial ?? '',
          leftSpringTorsion: s.leftSpringTorsion ?? '',
          leftSpringWarping: s.leftSpringWarping ?? '',
          rightSpringAxial: s.rightSpringAxial ?? '',
          rightSpringTorsion: s.rightSpringTorsion ?? '',
          rightSpringWarping: s.rightSpringWarping ?? '',
        })),
      );
    } else {
      setSpans(initialState.spans);
    }
    setAdvancedOptions((prev) => ({ ...prev, ...(ui.advancedOptions || {}) }));
    const mf = ui.materialFamily || 'steel';
    setMaterialFamily(mf);
    if (mf === 'steel') {
      const st = ui.selectedType || 'CFSHS';
      setSelectedType(st);
      const sid = ui.selectedCrossSectionId != null ? String(ui.selectedCrossSectionId) : '';
      window.setTimeout(() => setSelectedCrossSectionId(sid), 700);
    } else {
      if (ui.selectedType != null) setSelectedType(ui.selectedType);
      if (ui.selectedCrossSectionId != null) {
        setSelectedCrossSectionId(String(ui.selectedCrossSectionId));
      }
    }
    setFormErrors({});
    setResults(null);
    setLastSuccessfulCalcInputsKey(null);
  }, []);

  const removeSpanByIndex = useCallback((spanIndexToRemove) => {
      setSpans(currentSpans => {
          if (currentSpans.length <= 1) { 
              setNotification({ open: true, message: 'Cannot remove the last span.', severity: 'warning' });
              return currentSpans; // Keep at least one span
          }
          const newSpans = currentSpans.filter((_, index) => index !== spanIndexToRemove);
          
          // Adjust boundary condition and coefficient of the span that comes AFTER the removed one,
          // making it inherit from the span that came BEFORE the removed one.
          if (spanIndexToRemove > 0 && spanIndexToRemove < newSpans.length) { // Check if there's a span before and after the removed position
              const spanBeforeRemoved = currentSpans[spanIndexToRemove - 1];
              newSpans[spanIndexToRemove].leftBoundary = spanBeforeRemoved.rightBoundary;
              newSpans[spanIndexToRemove].leftSpringCoefficient = spanBeforeRemoved.rightSpringCoefficient; // Inherit coeff too
              newSpans[spanIndexToRemove].leftTorsionSupport = spanBeforeRemoved.rightTorsionSupport !== false;
              newSpans[spanIndexToRemove].leftWarpingSupport = false;
              newSpans[spanIndexToRemove].leftSpringAxial = spanBeforeRemoved.rightSpringAxial || '';
              newSpans[spanIndexToRemove].leftSpringTorsion = spanBeforeRemoved.rightSpringTorsion || '';
              newSpans[spanIndexToRemove].leftSpringWarping = spanBeforeRemoved.rightSpringWarping || '';
          } else if (spanIndexToRemove === 0 && newSpans.length > 0) {
              // If the first span was removed, the new first span doesn't inherit boundary condition
              // Ensure it has a valid default if needed (e.g., pinned) - although initial state handles this
          }

          return newSpans;
      });
  }, [setSpans, setNotification]);


  // --- Value provided by the context ---
  const contextValue = useMemo(() => {
    console.log('BeamContext: Creating/Recreating contextValue. The selectedSection being included is:', selectedSection);
    return {
    // State
    spans,
    selectedType,
    materialFamily,
    setMaterialFamily,
    selectedCrossSectionId,
    crossSections,
    results,
    loading,
    error,
    formErrors,
    notification,
    totalBeamLength,
    selectedSection,
    resultsOutOfDate,
    setLastSuccessfulCalcInputsKey,
    loadBeamFromSavedInput,

    // Updaters
    // setSpans, // Replaced by specific updaters below
    setSelectedType,
    setSelectedCrossSectionId,
    setCrossSections, // Keep for data fetching
    setResults,       // Keep for calculation results
    setLoading,       // Keep for async states
    setError,         // Keep for async states
    setFormErrors,    // Keep for direct error setting if needed, but handlers manage most
    setNotification,  // Keep for user feedback
    advancedOptions,
    setAdvancedOptions,
    setAdvancedOption,

    // Specific Handlers
    updateSpanLength,
    updateLoad,
    updateBoundary,
    updateTorsionSupport,
    updateWarpingSupport,
    updateSpringCoefficient,
    updateSpringExtra,
    addLoadToSpan,
    removeLoadFromSpan,
    addNewSpan,
    removeSpanByIndex,
    // calculateBeam (to be added/refactored)

  };
  }, [
    spans, selectedType, materialFamily, selectedCrossSectionId, crossSections, results,
    loading, error, formErrors, notification, totalBeamLength, selectedSection,
    resultsOutOfDate, setLastSuccessfulCalcInputsKey, loadBeamFromSavedInput,
    // Include handlers in dependency array for useMemo
    updateSpanLength, updateLoad, updateBoundary, updateTorsionSupport, updateWarpingSupport, updateSpringCoefficient, updateSpringExtra,
    addLoadToSpan, removeLoadFromSpan, addNewSpan, removeSpanByIndex,
    // Include setters that are still exposed
    setSelectedType, setSelectedCrossSectionId, setCrossSections, setResults,
    setLoading, setError, setFormErrors, setNotification,
    advancedOptions, setAdvancedOption
  ]);

  return (
    <BeamContext.Provider value={contextValue}>
      {children}
    </BeamContext.Provider>
  );
};

// 4. Export the Context (to be used by useContext)
export default BeamContext; 